import React, { FC, ReactNode, useMemo } from 'react';
import { FORM_FIELDS, LABELS } from '../../../constants';
import {
    amountTransform,
    onlyNumbersOutput,
    onlyRussianOutput,
    PhoneInput,
    SelectInput,
    TextInput,
} from '../../../components/landing.common';
import { Controller } from 'react-hook-form';
import { Control } from 'react-hook-form/dist/types/form';
import { SelectOption } from '@sravni/react-design-system/dist/types/types/Select.types';
import {
    AREA_OF_EMPLOYMENT_SOURCE,
    EMPLOYER_TYPE_SOURCES,
    EMPLOYMENT_TYPE_SOURCE,
    INCOME_CONFIRMATION_SOURCE,
    JOB_TYPE_SOURCE,
    LAST_EXPERIENCE_MONTHS_SOURCE,
    LAST_EXPERIENCE_YEARS_SOURCE,
    SENIORITY_SOURCE,
    UNEMPLOYMENT_REASON_SOURCE,
} from '../../../constants/sources';
import { FormOrganizationSuggest } from '../../../components/form/ui/suggest/organization';
import { FormAddressSuggest } from '../../../components/form/ui/suggest/address';
import { Tooltip } from '@sravni/react-design-system';
import { JobTitleSuggest } from '../../../components/form/ui/suggest/job.title';
import { FocusedFieldProps } from '../../../components/form/focused.view/types';

export type CommonFieldProps = {
    control: Control;
    error?: ReactNode | string;
    options?: Array<SelectOption>;
    onChange?: (event: FE.FieldChangeEvent, onChange: (...event: any[]) => void) => void | Promise<void>;
    onClear?: (onChange: (...event: any) => void) => void | Promise<void>;
    onBlur?: (event: FE.FieldChangeEvent, onBlur: (...event: any[]) => void, option?: any) => void | Promise<void>;
} & FocusedFieldProps;

type EmploymentTypeProps = Omit<CommonFieldProps, 'onBlur'> & {
    onBlur?: React.ComponentProps<typeof SelectInput>['onBlur'];
};
export const EmploymentTypeField: FC<EmploymentTypeProps> = ({ control, onBlur, error }) => {
    const employmentTypeOptions = useMemo(
        () => EMPLOYMENT_TYPE_SOURCE.map(({ name: label, value }) => ({ label, value })),
        [],
    );

    const onBlurHandler =
        (fieldOnBlur: (value?: any) => void): React.ComponentProps<typeof SelectInput>['onBlur'] =>
        (value) => {
            if (onBlur) {
                onBlur(value);
            }
            fieldOnBlur(value);
        };

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.EMPLOYMENT_TYPE}
            render={({ field: { onBlur: fieldOnBlur, ...field } }) => (
                <SelectInput
                    {...field}
                    onBlur={onBlurHandler(fieldOnBlur)}
                    withCrossIcon={false}
                    options={employmentTypeOptions}
                    label={LABELS[FORM_FIELDS.EMPLOYMENT_TYPE]}
                    errorMessage={error}
                />
            )}
        />
    );
};

export const UnEmploymentReasonField: FC<CommonFieldProps> = ({ control, error, ...other }) => {
    const unEmploymentReasonOptions = useMemo(
        () => UNEMPLOYMENT_REASON_SOURCE.map(({ name: label, value }) => ({ label, value })),
        [],
    );

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.UNEMPLOYMENT_REASON}
            render={({ field }) => (
                <SelectInput
                    {...field}
                    options={unEmploymentReasonOptions}
                    withCrossIcon={false}
                    label="Причина отсутствия занятости"
                    errorMessage={error}
                    {...(other as any)}
                />
            )}
        />
    );
};

type OrganizationSuggestProps = CommonFieldProps &
    Omit<
        React.ComponentProps<typeof FormOrganizationSuggest>,
        keyof CommonFieldProps | 'input' | 'count' | 'maxLength'
    >;

export const OrganizationNameField: FC<OrganizationSuggestProps> = ({
    control,
    error,
    onChange,
    onBlur,
    onClear,
    label,
    disabled,
    ...other
}) => {
    return (
        <Controller
            control={control}
            name={FORM_FIELDS.ORGANIZATION_NAME}
            render={({ field: { onBlur: fieldOnBlur, ...field } }) => (
                <FormOrganizationSuggest
                    {...other}
                    input={{
                        ...field,
                        label: typeof label === 'string' ? label : 'Название организации',
                        errorMessage: error,
                        ...{ disabled },
                    }}
                    onChange={onChange ? (event) => onChange(event, field.onChange) : field.onChange}
                    onBlur={onBlur ? (event, option) => onBlur(event, fieldOnBlur, option) : fieldOnBlur}
                    onClear={onClear ? () => onClear(field.onChange) : field.onChange}
                    maxLength={150}
                    count={5}
                />
            )}
        />
    );
};

export const EmployerTypeField: FC<CommonFieldProps> = ({ control, error, ...other }) => {
    const employerTypeOptions = useMemo(
        () => EMPLOYER_TYPE_SOURCES.map(({ name: label, value }) => ({ label, value })),
        [],
    );

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.EMPLOYER_TYPE}
            render={({ field }) => (
                <SelectInput
                    {...field}
                    withCrossIcon={false}
                    options={employerTypeOptions}
                    label="Тип организации"
                    errorMessage={error}
                    {...(other as any)}
                />
            )}
        />
    );
};

export const AreaOfEmploymentField: FC<CommonFieldProps> = ({ control, error, ...other }) => {
    const areaOfEmploymentOptions = useMemo(
        () => AREA_OF_EMPLOYMENT_SOURCE.map(({ name: label, value }) => ({ label, value })),
        [],
    );

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.AREA_OF_EMPLOYMENT}
            render={({ field }) => (
                <SelectInput
                    {...field}
                    label="Сфера деятельности"
                    withCrossIcon={false}
                    options={areaOfEmploymentOptions}
                    errorMessage={error}
                    {...(other as any)}
                />
            )}
        />
    );
};

export const EmployerInnField: FC<CommonFieldProps> = ({ control, error, ...other }) => {
    return (
        <Controller
            control={control}
            name={FORM_FIELDS.EMPLOYER_INN}
            render={({ field }) => (
                <TextInput
                    {...field}
                    label="ИНН организации"
                    errorMessage={error}
                    onChange={onlyNumbersOutput(field)}
                    additionalMessage="Необязательное поле"
                    {...(other as any)}
                />
            )}
        />
    );
};

export const AnotherUnemploymentReasonField: FC<CommonFieldProps> = ({ control, error, ...other }) => {
    return (
        <Controller
            control={control}
            name={FORM_FIELDS.ANOTHER_UNEMPLOYMENT_REASON}
            render={({ field }) => (
                <TextInput
                    {...field}
                    onChange={onlyRussianOutput(field)}
                    label="Другая причина"
                    errorMessage={error}
                    {...(other as any)}
                />
            )}
        />
    );
};

export const WorkAddressField: FC<CommonFieldProps> = ({ control, error, onBlur, ...other }) => {
    return (
        <Controller
            control={control}
            name={FORM_FIELDS.WORK_ADDRESS}
            render={({ field: { onBlur: fieldOnBlur, onChange, ...field } }) => (
                <FormAddressSuggest
                    input={{
                        ...field,
                        label: 'Рабочий адрес',
                        errorMessage: error,
                        ...other,
                    }}
                    onChange={onChange}
                    onBlur={onBlur ? (event, option) => onBlur(event, fieldOnBlur, option) : fieldOnBlur}
                />
            )}
        />
    );
};

export const LastExperienceStartMonthField: FC<CommonFieldProps> = ({ control, error }) => {
    const monthsOptions = useMemo(
        () => LAST_EXPERIENCE_MONTHS_SOURCE.map(({ name: label, value }) => ({ label, value })),
        [],
    );

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.LAST_EXPERIENCE_START_MONTH}
            render={({ field }) => (
                <SelectInput
                    {...field}
                    withCrossIcon={false}
                    options={monthsOptions}
                    label="Месяц"
                    errorMessage={error}
                />
            )}
        />
    );
};

export const LastExperienceStartYearField: FC<CommonFieldProps> = ({ control, error }) => {
    const yearsOptions = useMemo(
        () => LAST_EXPERIENCE_YEARS_SOURCE.map(({ name: label, value }) => ({ label, value })),
        [],
    );

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.LAST_EXPERIENCE_START_YEAR}
            render={({ field }) => (
                <SelectInput {...field} options={yearsOptions} withCrossIcon={false} label="Год" errorMessage={error} />
            )}
        />
    );
};

export const SeniorityField: FC<CommonFieldProps> = ({ control, error }) => {
    const seniorityOptions = useMemo(() => SENIORITY_SOURCE.map(({ name: label, value }) => ({ label, value })), []);

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.SENIORITY}
            render={({ field }) => (
                <Tooltip title="" content="Общий трудовой стаж - это период, когда вы работали официально">
                    <SelectInput
                        {...field}
                        label="Общий трудовой стаж"
                        withCrossIcon={false}
                        options={seniorityOptions}
                        errorMessage={error}
                        additionalMessage="Необязательное поле"
                    />
                </Tooltip>
            )}
        />
    );
};

export const WorkPhoneField: FC<CommonFieldProps> = ({ control, error, ...other }) => {
    return (
        <Controller
            control={control}
            name={FORM_FIELDS.WORK_PHONE}
            render={({ field }) => (
                <PhoneInput {...field} label="Рабочий телефон" errorMessage={error} {...(other as any)} />
            )}
        />
    );
};

export const JobTitleField: FC<CommonFieldProps> = ({ control, error, ...other }) => {
    return (
        <Controller
            control={control}
            name={FORM_FIELDS.JOB_TITLE}
            render={({ field: { onBlur, ...field } }) => (
                <JobTitleSuggest
                    input={{
                        ...field,
                        label: 'Название должности',
                        errorMessage: error,
                        ...other,
                    }}
                    onChange={field.onChange}
                    onBlur={onBlur}
                />
            )}
        />
    );
};

export const JobTypeField: FC<CommonFieldProps> = ({ control, error, ...other }) => {
    const jobTypeOptions = useMemo(() => JOB_TYPE_SOURCE.map(({ name: label, value }) => ({ label, value })), []);

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.JOB_TYPE}
            render={({ field }) => (
                <SelectInput
                    {...field}
                    label="Тип должности"
                    withCrossIcon={false}
                    options={jobTypeOptions}
                    errorMessage={error}
                    {...(other as any)}
                />
            )}
        />
    );
};

type MonthlySalaryFieldProps = Omit<CommonFieldProps, 'onBlur'> &
    Omit<React.ComponentProps<typeof TextInput>, Exclude<keyof CommonFieldProps, 'onBlur'> | 'errorMessage'>;

export const MonthlySalaryField: FC<MonthlySalaryFieldProps> = ({ control, error, onBlur, ...other }) => {
    return (
        <Controller
            control={control}
            name={FORM_FIELDS.MONTHLY_SALARY}
            render={({ field }) => (
                <TextInput
                    {...field}
                    label="Ежемесячный доход"
                    {...other}
                    value={amountTransform.input(field)}
                    onChange={amountTransform.output(field)}
                    onBlur={
                        onBlur
                            ? (...event) => {
                                  field.onBlur();
                                  onBlur(...event);
                              }
                            : field.onBlur
                    }
                    errorMessage={error}
                />
            )}
        />
    );
};

export const IncomeConfirmationField: FC<CommonFieldProps> = ({ control, error }) => {
    const incomeConfirmationOptions = useMemo(
        () => INCOME_CONFIRMATION_SOURCE.map(({ name: label, value }) => ({ label, value })),
        [],
    );

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.INCOME_CONFIRMATION}
            render={({ field }) => (
                <SelectInput
                    {...field}
                    label="Способ подтверждения дохода"
                    withCrossIcon={false}
                    options={incomeConfirmationOptions}
                    errorMessage={error}
                />
            )}
        />
    );
};
